import { OTHER_NODES_LABEL } from "@faire/help-center-self-serve/shared/other-node";
import { SelfServeNodes } from "@faire/help-center-self-serve/types";
import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Shipping } from "@faire/web--source/slate/icons/foundation/Shipping";
import React from "react";

import { ApplyForEoriIA } from "../instantAnswers/myAccount-applyForEori";
import { ManageEoriInfoIA } from "../instantAnswers/myAccount-manageEoriInfo";
import { cancelOrder } from "../instantAnswers/ordersAndShipping-cancelOrder";
import { ChargesForDamagedMissingItemsIA } from "../instantAnswers/ordersAndShipping-chargesForDamagedOrMissingItems";
import { CreateReturnIA } from "../instantAnswers/ordersAndShipping-createReturn";
import { DutiesInvoiceIA } from "../instantAnswers/ordersAndShipping-dutiesInvoice";
import { EoriDetailsIA } from "../instantAnswers/ordersAndShipping-eoriDetails";
import { ExtendReturnIA } from "../instantAnswers/ordersAndShipping-extendReturn";
import { HowDutiesWorkIA } from "../instantAnswers/ordersAndShipping-howDutiesWork";
import { ItemDelayedIA } from "../instantAnswers/ordersAndShipping-itemDelayed";
import { ReportDamagedItemIA } from "../instantAnswers/ordersAndShipping-reportDamagedItem";
import { ReportMissingItemIA } from "../instantAnswers/ordersAndShipping-reportMissingItem";
import { ReportWrongItemIA } from "../instantAnswers/ordersAndShipping-reportWrongItem";
import { RequestDutiesReimbursementIA } from "../instantAnswers/ordersAndShipping-requestDutiesReimbursement";
import { ShippingCostEstimatesIA } from "../instantAnswers/ordersAndShipping-shippingCostEstimates";
import { UpdateAccountShippingAddressIA } from "../instantAnswers/ordersAndShipping-updateAccountShippingAddress";
import { WhereIsMyOrderIA } from "../instantAnswers/ordersAndShipping-whereIsMyOrder";
import { GeneralContactBrandIA } from "../instantAnswers/shared-generalContactBrand";
import { ManageEoriInfoVisibilityFn } from "../visibilityFunctions/manageEoriInfoVisibilityFn";

export const useOrdersRetailerNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    Orders: {
      id: "Orders",
      label: strictLocalize({
        defaultMessage: "Orders",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Shipping fontSize="24px" color="faireBlack" />,
      children: [
        "TrackOrder",
        "OrderDelayed",
        "DamagedMissingOrWrongItems",
        "CancelOrder",
        "Returns",
        "InternationalOrdersCustomsAndDuties",
        "EditOrderOrShippingAddress",
        "ShippingCosts",
        "ReturnedToSender",
      ],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Orders issue",
        description: {
          text: "Title for displaying on the order summary sidebar",
        },
      }),
    },
    TrackOrder: {
      id: "TrackOrder",
      label: strictLocalize({
        defaultMessage: "Track order",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: WhereIsMyOrderIA(strictLocalize),
      level: 2,
    },
    CancelOrder: {
      id: "CancelOrder",
      label: strictLocalize({
        defaultMessage: "Cancel order",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: cancelOrder(strictLocalize),
      articles: [articles.MyOrders.CANCELING_AN_ORDER],
      level: 2,
    },
    EditOrderOrShippingAddress: {
      id: "EditOrderOrShippingAddress",
      label: strictLocalize({
        defaultMessage: "Edit order or shipping address",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["UpdateShippingAddress"],
      level: 2,
    },
    InternationalOrdersCustomsAndDuties: {
      id: "InternationalOrdersCustomsAndDuties",
      label: strictLocalize({
        defaultMessage: "International orders, customs & duties",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "Duties",
        "EORI",
        "AboutImportingProducts",
        "OtherInternationalShippingAndCustoms",
      ],
      level: 2,
    },
    ShippingCosts: {
      id: "ShippingCosts",
      label: strictLocalize({
        defaultMessage: "Shipping costs",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "ShippingCostEstimates",
        "ShippingCostsWithInsider",
        "OtherShippingCosts",
      ],
      level: 2,
    },
    DamagedMissingOrWrongItems: {
      id: "DamagedMissingOrWrongItems",
      label: strictLocalize({
        defaultMessage: "Damaged, missing, or wrong items",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "ReportDamagedItems",
        "ReportMissingItems",
        "WrongItemsSent",
        "ChargesForDamagedOrMissingItems",
        "OtherDamagedMissingOrWrong",
      ],
      level: 2,
    },
    Returns: {
      id: "Returns",
      label: strictLocalize({
        defaultMessage: "Returns",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["StartAReturn", "ExtendReturnWindow", "OtherReturns"],
      level: 2,
    },
    UpdateShippingAddress: {
      id: "UpdateShippingAddress",
      label: strictLocalize({
        defaultMessage: "Update shipping address on the account",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.SHIPPING_ADDRESS],
      instantAnswer: UpdateAccountShippingAddressIA(strictLocalize),
      level: 3,
    },
    StartAReturn: {
      id: "StartAReturn",
      label: strictLocalize({
        defaultMessage: "Start a return",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.ReturnsAndRefunds.MAKE_A_RETURN,
        articles.ReturnsAndRefunds.CANT_RETURN,
      ],
      instantAnswer: CreateReturnIA(strictLocalize),
      level: 3,
    },
    ExtendReturnWindow: {
      id: "ExtendReturnWindow",
      label: strictLocalize({
        defaultMessage: "Extend return window",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ReturnsAndRefunds.EXTEND_A_RETURN],
      instantAnswer: ExtendReturnIA(strictLocalize),
      level: 3,
    },
    OtherReturns: {
      id: "OtherReturns",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.ReturnsAndRefunds.MAKE_A_RETURN],
      instantAnswer: GeneralContactBrandIA(strictLocalize),
      level: 3,
    },
    ReportDamagedItems: {
      id: "ReportDamagedItems",
      label: strictLocalize({
        defaultMessage: "Report damaged items",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      instantAnswer: ReportDamagedItemIA(strictLocalize),
      level: 3,
    },
    ReportMissingItems: {
      id: "ReportMissingItems",
      label: strictLocalize({
        defaultMessage: "Report missing items",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      instantAnswer: ReportMissingItemIA(strictLocalize),
      level: 3,
    },
    WrongItemsSent: {
      id: "WrongItemsSent",
      label: strictLocalize({
        defaultMessage: "Wrong items sent",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      instantAnswer: ReportWrongItemIA(strictLocalize),
      level: 3,
    },
    ChargesForDamagedOrMissingItems: {
      id: "ChargesForDamagedOrMissingItems",
      label: strictLocalize({
        defaultMessage: "Charges for damaged or missing items",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      instantAnswer: ChargesForDamagedMissingItemsIA(strictLocalize),
      level: 3,
    },
    OtherDamagedMissingOrWrong: {
      id: "OtherDamagedMissingOrWrong",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      instantAnswer: GeneralContactBrandIA(strictLocalize),
      level: 3,
    },

    OrderDelayed: {
      id: "OrderDelayed",
      label: strictLocalize({
        defaultMessage: "Order delayed",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.ORDER_STATUS],
      instantAnswer: ItemDelayedIA(strictLocalize),
      level: 2,
    },
    ReturnedToSender: {
      id: "ReturnedToSender",
      label: strictLocalize({
        defaultMessage: "Returned to sender",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ReturnsAndRefunds.MAKE_A_RETURN],
      instantAnswer: GeneralContactBrandIA(strictLocalize),
      level: 2,
    },
    ShippingCostEstimates: {
      id: "ShippingCostEstimates",
      label: strictLocalize({
        defaultMessage: "Understanding shipping cost estimates",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.SHIPPING_WORK],
      instantAnswer: ShippingCostEstimatesIA(strictLocalize),
      level: 3,
    },
    ShippingCostsWithInsider: {
      id: "ShippingCostsWithInsider",
      label: strictLocalize({
        defaultMessage: "Shipping costs with insider",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQ.FAIRE_INSIDER_EU, articles.FAQ.FAIRE_INSIDER_US],
      level: 3,
    },
    OtherShippingCosts: {
      id: "OtherShippingCosts",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.FulfillingOrders.SHIPPING_WORK],
      level: 3,
    },
    Duties: {
      id: "Duties",
      label: strictLocalize({
        defaultMessage: "Duties",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 3,
      children: [
        "AboutDuties",
        "DutiesNotice",
        "RequestDutiesReimbursement",
        "OtherInternationalShippingAndCustomsDuties",
      ],
    },
    EORI: {
      id: "EORI",
      label: strictLocalize({
        defaultMessage: "EORI",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["AboutEORI", "ApplyForEORI", "ManageEORI"],
      level: 3,
    },
    AboutImportingProducts: {
      id: "AboutImportingProducts",
      label: strictLocalize({
        defaultMessage: "About importing products",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.ImportFeesAndDuties.USA,
        articles.ImportFeesAndDuties.Canada,
        articles.ImportFeesAndDuties.Europe,
        articles.ImportFeesAndDuties.Australia,
      ],
      instantAnswer: GeneralContactBrandIA(strictLocalize),
      level: 3,
    },
    OtherInternationalShippingAndCustoms: {
      id: "OtherInternationalShippingAndCustoms",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.ImportFeesAndDuties.DUTIES_REFUND],
      level: 3,
    },
    ApplyForEORI: {
      id: "ApplyForEORI",
      label: strictLocalize({
        defaultMessage: "Apply for EORI",
        description: {
          text: "A fifth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ApplyForEoriIA(strictLocalize, articles),
      isNodeVisible: ManageEoriInfoVisibilityFn,
      level: 4,
    },
    ManageEORI: {
      id: "ManageEORI",
      label: strictLocalize({
        defaultMessage: "Manage EORI",
        description: {
          text: "A fifth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ManageEoriInfoIA(strictLocalize),
      isNodeVisible: ManageEoriInfoVisibilityFn,
      level: 4,
    },
    AboutEORI: {
      id: "AboutEORI",
      label: strictLocalize({
        defaultMessage: "EORI details",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ImportFeesAndDuties.EORI],
      instantAnswer: EoriDetailsIA(strictLocalize),
      level: 4,
    },
    AboutDuties: {
      id: "AboutDuties",
      label: strictLocalize({
        defaultMessage: "About duties",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ImportFeesAndDuties.DUTIES_REFUND],
      instantAnswer: HowDutiesWorkIA(strictLocalize, articles),
      level: 4,
    },
    DutiesNotice: {
      id: "DutiesNotice",
      label: strictLocalize({
        defaultMessage: "Duties notice",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.ImportFeesAndDuties.DUTIES_REFUND?.atSection("0"),
        articles.FAQ.FAIRE_INSIDER_EU?.atSection("6"),
      ],
      instantAnswer: DutiesInvoiceIA(strictLocalize),
      level: 4,
    },
    RequestDutiesReimbursement: {
      id: "RequestDutiesReimbursement",
      label: strictLocalize({
        defaultMessage: "Request duties reimbursement",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ImportFeesAndDuties.DUTIES_REFUND],
      instantAnswer: RequestDutiesReimbursementIA(strictLocalize, articles),
      level: 4,
    },
    OtherInternationalShippingAndCustomsDuties: {
      id: "OtherInternationalShippingAndCustomsDuties",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.ImportFeesAndDuties.DUTIES_REFUND],
      level: 4,
    },
  };
};
